// Dexie wrapper for PageVoice. Two tables:
//   books          — light metadata, indexed by id
//   bookBlobs      — heavy blobs (epub file + cover), keyed by id, lazy-loaded
//   positions      — per-book last reading position
//   settings       — singleton key/value for reader + TTS prefs
//
// Keeping blobs in a separate table means listing the library never has
// to pull megabytes of EPUB data into memory.

import Dexie, { type Table } from "dexie";
import type { Book, BookBlobRecord, ReadingPosition } from "@/types/book";
import type { ReaderSettings } from "@/types/reader";
import type { TTSSettings } from "@/types/tts";
import type { Bookmark, Highlight } from "@/types/highlight";
import type {
  BookStats,
  PerBookPrefs,
  ReadingSession,
} from "@/types/stats";

interface SettingsRecord {
  key: "reader" | "tts";
  value: ReaderSettings | TTSSettings;
}

/** Cached page translation — the durable L2 behind the localStorage L1
 *  in TranslatePanel. Keyed by (bookId | source-text hash | target lang |
 *  translator version) baked into `key` so a translator upgrade can
 *  invalidate old rows wholesale. `updatedAt` is touched on read so LRU
 *  pruning keeps the pages the user actually revisits. */
export interface TranslationRecord {
  key: string;
  bookId: string;
  lang: string;
  translated: string;
  updatedAt: number;
}

/** Whole-book pre-translation for an imported non-English book: a list of
 *  {source paragraph, English} pairs built once in the background so the book
 *  opens as instant English with working audio. Keyed by bookId. */
export interface PretranslationRecord {
  bookId: string;
  lang: string;
  pairs: { s: string; e: string }[];
  updatedAt: number;
}

/** Original audio for a voice-note (VISION.md §3 keeps the audio, not just the
 *  transcription). Local-only — the blob never leaves the device. Linked to a
 *  highlight; a highlight may have several. */
export interface AudioNoteRecord {
  id: string;
  highlightId: string;
  bookId: string;
  blob: Blob;
  mimeType: string;
  createdAt: number;
}

class PageVoiceDB extends Dexie {
  books!: Table<Book, string>;
  bookBlobs!: Table<BookBlobRecord, string>;
  positions!: Table<ReadingPosition, string>;
  settings!: Table<SettingsRecord, string>;
  highlights!: Table<Highlight, string>;
  bookmarks!: Table<Bookmark, string>;
  sessions!: Table<ReadingSession, string>;
  bookStats!: Table<BookStats, string>;
  bookPrefs!: Table<PerBookPrefs, string>;
  translations!: Table<TranslationRecord, string>;
  pretranslations!: Table<PretranslationRecord, string>;
  audioNotes!: Table<AudioNoteRecord, string>;

  constructor() {
    super("pagevoice");
    this.version(1).stores({
      books: "id, importedAt, lastOpenedAt, title, author",
      bookBlobs: "id",
      positions: "bookId, updatedAt",
      settings: "key",
    });
    // v2 — highlights + bookmarks for the notes feature.
    this.version(2).stores({
      books: "id, importedAt, lastOpenedAt, title, author",
      bookBlobs: "id",
      positions: "bookId, updatedAt",
      settings: "key",
      highlights: "id, bookId, createdAt, updatedAt",
      bookmarks: "id, bookId, createdAt",
    });
    // v3 — reading stats + per-book prefs.
    this.version(3).stores({
      books: "id, importedAt, lastOpenedAt, title, author",
      bookBlobs: "id",
      positions: "bookId, updatedAt",
      settings: "key",
      highlights: "id, bookId, createdAt, updatedAt",
      bookmarks: "id, bookId, createdAt",
      sessions: "id, bookId, startedAt, day",
      bookStats: "id, lastDay",
      bookPrefs: "id",
    });
    // v4 — persistent translation cache. Rows are small (≤ ~7 KB of
    // translated text each); pruned per-book by TranslatePanel's writer.
    this.version(4).stores({
      books: "id, importedAt, lastOpenedAt, title, author",
      bookBlobs: "id",
      positions: "bookId, updatedAt",
      settings: "key",
      highlights: "id, bookId, createdAt, updatedAt",
      bookmarks: "id, bookId, createdAt",
      sessions: "id, bookId, startedAt, day",
      bookStats: "id, lastDay",
      bookPrefs: "id",
      translations: "key, bookId, updatedAt",
    });
    // v5 — whole-book pre-translations for imported non-English books.
    this.version(5).stores({
      books: "id, importedAt, lastOpenedAt, title, author",
      bookBlobs: "id",
      positions: "bookId, updatedAt",
      settings: "key",
      highlights: "id, bookId, createdAt, updatedAt",
      bookmarks: "id, bookId, createdAt",
      sessions: "id, bookId, startedAt, day",
      bookStats: "id, lastDay",
      bookPrefs: "id",
      translations: "key, bookId, updatedAt",
      pretranslations: "bookId, lang, updatedAt",
    });
    // v6 — annotation model: index highlights by kind (highlight/review) and
    // multiEntry by tags, so the notebook can filter by tag and reviews can be
    // queried without scanning. Additive; existing rows migrate untouched.
    this.version(6).stores({
      books: "id, importedAt, lastOpenedAt, title, author",
      bookBlobs: "id",
      positions: "bookId, updatedAt",
      settings: "key",
      highlights: "id, bookId, createdAt, updatedAt, kind, *tags",
      bookmarks: "id, bookId, createdAt",
      sessions: "id, bookId, startedAt, day",
      bookStats: "id, lastDay",
      bookPrefs: "id",
      translations: "key, bookId, updatedAt",
      pretranslations: "bookId, lang, updatedAt",
    });
    // v7 — preserved voice-note audio (local-only blobs, linked to highlights).
    this.version(7).stores({
      books: "id, importedAt, lastOpenedAt, title, author",
      bookBlobs: "id",
      positions: "bookId, updatedAt",
      settings: "key",
      highlights: "id, bookId, createdAt, updatedAt, kind, *tags",
      bookmarks: "id, bookId, createdAt",
      sessions: "id, bookId, startedAt, day",
      bookStats: "id, lastDay",
      bookPrefs: "id",
      translations: "key, bookId, updatedAt",
      pretranslations: "bookId, lang, updatedAt",
      audioNotes: "id, highlightId, bookId, createdAt",
    });
  }
}

export const db = new PageVoiceDB();
